import React from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

// Import configs and util modules
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { CATEGORY_ACCESSORY, CATEGORY_ACCESSORY_OLD, CATEGORY_ATTIRE, CATEGORY_DRESSES, CATEGORY_GOWNS, CATEGORY_OCCASIONS, CATEGORY_SHOES, DAMAGE, DRESS, LABELS, PRODUCT_DEATILS, propTypes, styleCategories, styleSubAccessories } from '../../../../util/types';

// Import shared components
import { Form, Button, FieldTextInput } from '../../../../components';

// Import modules from this directory
import css from './EditListingSizeForm.module.css';
import FieldMultiSelect from '../../../../components/FieldMultiSelect/FieldMultiSelect';
import { composeValidators, maxLength, minLength, required } from '../../../../util/validators';

const DESCRIPTION_MAX_LENGTH = 750;
const DESCRIPTION_MIN_LENGTH = 15;

export const EditListingSizeFormComponent = props => (
  <FinalForm
    {...props}
    keepDirtyOnReinitialize={true}
    render={formRenderProps => {
      const {
        formId,
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        values,
        productType,
        setSaveAsDraft,
        saveAsDraft,
        onSaveDraft,
        history,
        listing,
        page
      } = formRenderProps;
      const { listingColor = [], listingSize = [], listingMaterial = [], listingStreetSize = [], listingHeelHeight = [], listingLength = [], listingSleeves = [], listingNeckline = [], listingSubAccessories = [], listingStyle = [] } = page?.allFieldsData || {};

      const { updateListingError, showListingsError } = fetchErrors || {};
      const { size, streetSize, material, color, length, sleeves, neckline, anyDamage, description, heelHeight, style, alterations } = values || {};

      const { category, style: filterStyle, otherImages } = (listing && listing.id && listing.attributes.publicData) || {};
      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress && !saveAsDraft;
      const submitDisabled =
        invalid || disabled || submitInProgress;

      const itemRequired = required(
        intl.formatMessage({
          id: 'EditListingDetailsForm.itemRequired',
        })
      );

      const maxLengthMessage = intl.formatMessage(
        { id: 'EditListingDetailsForm.maxLength' },
        {
          maxLength: DESCRIPTION_MAX_LENGTH,
        }
      );
      const maxLength750Message = maxLength(maxLengthMessage, DESCRIPTION_MAX_LENGTH);

      const minLengthMessage = intl.formatMessage(
        { id: 'EditListingDetailsForm.minLength' },
        {
          minLength: DESCRIPTION_MIN_LENGTH,
        }
      );
      const minLength15Message = minLength(minLengthMessage, DESCRIPTION_MIN_LENGTH);

      const fieldProps =
        productType === CATEGORY_SHOES
          ? {
            labelId: 'EditListingSizeForm.heelHeight',
            fieldId: 'heelHeight',
            options: listingHeelHeight.sort((a, b) => {
              const getStartValue = key => {
                if (key === 'other') return Infinity;
                const match = key.match(/^(\d+)/); // capture the starting number
                return match ? parseInt(match[1], 10) : Infinity;
              };

              return getStartValue(a.key) - getStartValue(b.key);
            }),
            defaultValue: heelHeight || '',
            validate: itemRequired,
          }
          : {
            labelId: 'EditListingSizeForm.streetSize',
            fieldId: 'streetSize',
            options: listingStreetSize.sort((a, b) => {
              const aVal = a.key.includes('+') ? Infinity : parseInt(a.key, 10);
              const bVal = b.key.includes('+') ? Infinity : parseInt(b.key, 10);
              return aVal - bVal;
            }),
            defaultValue: streetSize || '',
            validate: itemRequired,
          };

      const fieldProps1 = {
        labelId: 'EditListingAppearanceForm.material',
        fieldId: 'material',
        options:
          listingMaterial.filter((e) => e.parentKey.includes(productType)).length > 0
            ? listingMaterial.filter((e) => e.parentKey.includes(productType)).sort((a, b) => a.label.localeCompare(b.label))
            : listingMaterial.sort((a, b) => a.label.localeCompare(b.label)),
        defaultValue: material || '',
      };

      const filteredColor = listingColor
        .filter(s => s.parentKey.includes(productType)) || [];
      const filterStyles = listingStyle.filter(event => (event.parentKey.includes(productType)));
      const filterAccessoriesStyle = listingSubAccessories.filter(e => e.parentKey == productType && e.parentCategory == category?.label);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {updateListingError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingLocationForm.updateFailed" />
            </p>
          ) : null}

          {showListingsError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingLocationForm.showListingFailed" />
            </p>
          ) : null}
          {![CATEGORY_ACCESSORY, CATEGORY_ACCESSORY_OLD].includes(productType) ? <>
            <div className={css.fieldSelect}>
              <div>
                <label>{[CATEGORY_GOWNS].includes(productType) ? <FormattedMessage id="EditListingSizeForm.size" />
                  : <FormattedMessage id="EditListingSizeForm.occSize" />}</label>
                <FieldMultiSelect
                  isMulti={false}
                  isRequired={true}
                  className={css.input}
                  id={'size'}
                  name={'size'}
                  fontClass={css.fontClass}
                  options={listingSize?.filter(e => e.parentKey.includes(productType)).sort((a, b) => {
                    const aVal = a.key.includes('+') ? Infinity : parseFloat(a.key);
                    const bVal = b.key.includes('+') ? Infinity : parseFloat(b.key);
                    return aVal - bVal;
                  })}
                  autoComplete={'size'}
                  defaultValue={size || ''}
                  validate={itemRequired}
                />
              </div>
              {![CATEGORY_OCCASIONS].includes(productType) ? <div>
                <label>
                  <FormattedMessage id={fieldProps.labelId} />
                </label>
                <FieldMultiSelect
                  isMulti={false}
                  isRequired={true}
                  className={css.input}
                  id={fieldProps.fieldId}
                  name={fieldProps.fieldId}
                  fontClass={css.fontClass}
                  options={fieldProps.options}
                  autoComplete={fieldProps.fieldId}
                  defaultValue={fieldProps.defaultValue}
                  validate={fieldProps.validate}
                />
              </div> : null}
            </div>
            <div className={css.fieldSelect}>
              {![CATEGORY_OCCASIONS, CATEGORY_SHOES].includes(productType) ? <div>
                <label>
                  <FormattedMessage id={fieldProps1.labelId} />
                </label>
                <FieldMultiSelect
                  isMulti={false}
                  isRequired={true}
                  className={css.input}
                  id={fieldProps1.fieldId}
                  name={fieldProps1.fieldId}
                  fontClass={css.fontClass}
                  options={fieldProps1.options}
                  autoComplete={fieldProps1.fieldId}
                  defaultValue={fieldProps1.defaultValue}
                  validate={itemRequired}
                />
              </div> : null}
              <div>
                <label><FormattedMessage id="EditListingAppearanceForm.color" /></label>
                <FieldMultiSelect
                  isMulti={false}
                  isRequired={true}
                  className={css.input}
                  id={'color'}
                  name={'color'}
                  fontClass={css.fontClass}
                  options={filteredColor?.length > 0 ? filteredColor.sort((a, b) => a.label.localeCompare(b.label)) : listingColor.sort((a, b) => a.label.localeCompare(b.label))}
                  autoComplete={'color'}
                  defaultValue={color || ''}
                  validate={itemRequired}
                />
              </div>
            </div>
            {([CATEGORY_GOWNS].includes(productType) || styleSubAccessories.includes(category?.label)) ? (<div>
              <label><FormattedMessage id={"EditListingAppearanceForm.style"} /></label>
              <FieldMultiSelect
                isMulti={false}
                isRequired={true}
                className={css.description}
                id={'style'}
                name={'style'}
                fontClass={css.fontClass}
                options={(styleSubAccessories.includes(category?.label) && !Array.isArray(category)) ? filterAccessoriesStyle.sort((a, b) => a.label.localeCompare(b.label)) : filterStyles.sort((a, b) => a.label.localeCompare(b.label))}
                autoComplete={'style'}
                defaultValue={style || ''}
                validate={itemRequired}
              />
            </div>) : null}

            {[CATEGORY_GOWNS, CATEGORY_OCCASIONS, CATEGORY_ATTIRE, CATEGORY_DRESSES].includes(productType) ? <>

              {
                styleCategories.includes((style?.label || filterStyle)) ? <div className={css.fieldSelect}> <div>
                  <label><FormattedMessage id="EditListingAppearanceForm.length" /></label>
                  <FieldMultiSelect
                    isMulti={false}
                    isRequired={true}
                    className={css.input}
                    id={'length'}
                    name={'length'}
                    fontClass={css.fontClass}
                    options={listingLength.sort((a, b) => a.label.localeCompare(b.label))}
                    autoComplete={'length'}
                    defaultValue={length || ''}
                    validate={itemRequired}
                  />
                </div>   </div> : null
              }



              {([style?.label, filterStyle].includes(DRESS)) ? <div className={css.fieldSelect}>
                <div>
                  <label><FormattedMessage id="EditListingAppearanceForm.sleeves" /></label>
                  <FieldMultiSelect
                    isMulti={false}
                    isRequired={true}
                    className={css.input}
                    id={'sleeves'}
                    name={'sleeves'}
                    fontClass={css.fontClass}
                    options={listingSleeves.sort((a, b) => a.label.localeCompare(b.label))}
                    autoComplete={'sleeves'}
                    defaultValue={sleeves || ''}
                    validate={itemRequired}
                  />
                </div>
              </div> : null}
            </> : CATEGORY_OCCASIONS == productType ?
              <div className={css.fieldSelect}>
                <div>
                  <label><FormattedMessage id="EditListingAppearanceForm.sleeves" /></label>
                  <FieldMultiSelect
                    isMulti={false}
                    isRequired={true}
                    className={css.input}
                    id={'sleeves'}
                    name={'sleeves'}
                    fontClass={css.fontClass}
                    options={listingSleeves.sort((a, b) => a.label.localeCompare(b.label))}
                    autoComplete={'sleeves'}
                    defaultValue={sleeves || ''}
                    validate={itemRequired}
                  />
                </div>

                <div>
                  <label><FormattedMessage id="EditListingAppearanceForm.length" /></label>
                  <FieldMultiSelect
                    isMulti={false}
                    isRequired={true}
                    className={css.input}
                    id={'length'}
                    name={'length'}
                    fontClass={css.fontClass}
                    options={listingLength.sort((a, b) => a.label.localeCompare(b.label))}
                    autoComplete={'length'}
                    defaultValue={length || ''}
                    validate={itemRequired}
                  />
                </div>
              </div>
              : null}

            {CATEGORY_GOWNS == productType ? <div className={css.fieldSelect}>
              <div>
                <label><FormattedMessage id="EditListingAppearanceForm.sleeves" /></label>
                <FieldMultiSelect
                  isMulti={false}
                  isRequired={true}
                  className={css.input}
                  id={'sleeves'}
                  name={'sleeves'}
                  fontClass={css.fontClass}
                  options={listingSleeves.sort((a, b) => a.label.localeCompare(b.label))}
                  autoComplete={'sleeves'}
                  defaultValue={sleeves || ''}
                  validate={itemRequired}
                />
              </div>

              <div>
                <label><FormattedMessage id="EditListingAppearanceForm.neckline" /></label>
                <FieldMultiSelect
                  isMulti={false}
                  isRequired={true}
                  className={css.input}
                  id={'neckline'}
                  name={'neckline'}
                  fontClass={css.fontClass}
                  options={listingNeckline.sort((a, b) => a.label.localeCompare(b.label))}
                  autoComplete={'neckline'}
                  defaultValue={neckline || ''}
                  validate={itemRequired}
                />
              </div>
            </div>
              : null}
            {[CATEGORY_GOWNS, CATEGORY_DRESSES].includes(productType) ? <FieldTextInput
              id="alterations"
              name="alterations"
              className={css.description}
              type="textarea"
              label={intl.formatMessage({ id: 'EditListingAppearanceForm.alterations' })}
              placeholder={intl.formatMessage({
                id: 'EditListingDetailsForm.alterationsPlaceholder',
              })}
            // validate={composeValidators(itemRequired, minLength20Message)}
            /> : null}
          </> : null}

          <FieldTextInput
            id={`${formId}description`}
            name="description"
            className={css.description}
            type="textarea"
            label={intl.formatMessage({ id: 'EditListingDetailsForm.description' })}
            placeholder={intl.formatMessage({
              id: 'EditListingDetailsForm.descriptionPlaceholder',
            })}
            validate={composeValidators(itemRequired, maxLength750Message, minLength15Message)}
          />

          <FieldTextInput
            id="anyDamage"
            name="anyDamage"
            className={css.description}
            type="textarea"
            label={intl.formatMessage({ id: 'EditListingAppearanceForm.anyDamage' })}
            placeholder={intl.formatMessage({
              id: 'EditListingDetailsForm.damagePlaceholder',
            })}
          // validate={composeValidators(itemRequired, minLength20Message)}
          />

          {invalid ? (
            <span className={css.error}>
              <FormattedMessage id="EditListingSizeForm.errorText" values={{ TabName: <b>{PRODUCT_DEATILS}</b> }} />
            </span>
          ) : null}
          <div className={css.buttonWrapper}>
            <Button
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={submitReady}
            >
              {saveActionMsg}
            </Button>

            <Button
              className={css.draftButton}
              type="button"
              disabled={submitDisabled}
              inProgress={saveAsDraft}
              onClick={() => {
                setSaveAsDraft(true)
                const publicData = LABELS.reduce((acc, key) => {
                  const value = values[key]?.label;
                  if (value) {
                    acc[key] = value;
                  }
                  return acc;
                }, {
                  updatedAt: Math.floor(Date.now() / 1000),
                  anyDamage,
                });

                const updateValues = {
                  description,
                  publicData: {
                    ...publicData,
                    length: (styleCategories.includes(style?.label || filterStyle)) ? length?.label : null,
                    style: style?.label ? style?.label : filterStyle ? filterStyle : null,
                   otherImages: (!anyDamage?.trim() && otherImages && otherImages.length > 0) ? otherImages.filter((e) => e.imageType != DAMAGE) : otherImages,
                    alterations: alterations ? alterations : null
                  },
                };
                onSaveDraft(updateValues).then(() => {
                  history.push('/listings')
                })
              }}
            >
              <FormattedMessage id="EditListingsDetailsForm.saveAsDraft" />
            </Button>
          </div>

        </Form>
      );
    }}
  />
);

EditListingSizeFormComponent.defaultProps = {
  selectedPlace: null,
  fetchErrors: null,
  formId: 'EditListingSizeForm',
};

EditListingSizeFormComponent.propTypes = {
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  selectedPlace: propTypes.place,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditListingSizeFormComponent);
